/**
 * チャンギ (Janggi) の TypeScript 表現 (mixed-engine の serde 形と一致)。
 *
 * - 盤: `board[rank][file]` = `[10][9]`、 rank=0 が Cho 最下段、 rank=9 が Han 最下段。
 * - Side: 'First' = Cho (青/楚)、 'Second' = Han (赤/漢)。
 * - 점수は整数で ×10 保持 (덤 1.5 = 15)。
 * - 빅장 (両王対峙) は state.bikjang_pending に最後にそれを成立させた側を持つ。
 *   次手番でパス系 or 維持手を選ぶと「점수제 자동移行」 = ScoreOutcome.BikjangConfirmed。
 */

export type Side = 'First' | 'Second';

export type JanggiPieceKind =
  | 'King'
  | 'Advisor'
  | 'Chariot'
  | 'Cannon'
  | 'Horse'
  | 'Elephant'
  | 'Soldier';

export interface JanggiPiece {
  kind: JanggiPieceKind;
  owner: Side;
}

export type JanggiSetupVariant =
  | 'WonangMa'
  | 'YangGwima'
  | 'OenSangCharim'
  | 'OreunSangCharim';

export type JanggiWinReason = 'Checkmate' | 'Resign' | 'Timeout' | 'Disconnect';
export type JanggiScoreReason =
  | 'BikjangConfirmed'
  | 'RepetitionThreshold'
  | 'TwoPasses'
  | 'PlyCap';

export type JanggiOutcome =
  | { kind: 'Win'; winner: Side; reason: JanggiWinReason }
  | {
      kind: 'Score';
      score_cho_x10: number;
      score_han_x10: number;
      winner: Side;
      reason: JanggiScoreReason;
    };

export interface JanggiState {
  board: (JanggiPiece | null)[][];
  side: Side;
  move_number: number;
  pass_count: number;
  setup_cho: JanggiSetupVariant;
  setup_han: JanggiSetupVariant;
  deom_han_x10: number;
  bikjang_pending: Side | null;
  current_hash: string;
  position_count: Record<string, number>;
  outcome: JanggiOutcome | null;
}

export type JanggiMove =
  | { Move: { from: [number, number]; to: [number, number] } }
  | 'Pass';

export const JANGGI_FILES = 9;
export const JANGGI_RANKS = 10;

export function isJanggiMove(
  m: JanggiMove,
): m is { Move: { from: [number, number]; to: [number, number] } } {
  return typeof m === 'object' && m !== null && 'Move' in m;
}

export function isJanggiPass(m: JanggiMove): m is 'Pass' {
  return m === 'Pass';
}

/** 駒種 → 점수 (덤と独立、 整数)。 */
export function janggiPieceScore(kind: JanggiPieceKind): number {
  switch (kind) {
    case 'King':
      return 0;
    case 'Advisor':
      return 3;
    case 'Chariot':
      return 13;
    case 'Cannon':
      return 7;
    case 'Horse':
      return 5;
    case 'Elephant':
      return 3;
    case 'Soldier':
      return 2;
  }
}

/** 各 side の盤上生存駒の点数合計 (덤抜き)。 */
export function janggiScores(state: JanggiState): { cho: number; han: number } {
  let cho = 0;
  let han = 0;
  for (const row of state.board) {
    for (const cell of row) {
      if (!cell) continue;
      const s = janggiPieceScore(cell.kind);
      if (cell.owner === 'First') cho += s;
      else han += s;
    }
  }
  return { cho, han };
}

/** 駒種別個数 (1 side ぶん)。 */
export function janggiPieceCounts(
  state: JanggiState,
  side: Side,
): Record<JanggiPieceKind, number> {
  const counts: Record<JanggiPieceKind, number> = {
    King: 0,
    Advisor: 0,
    Chariot: 0,
    Cannon: 0,
    Horse: 0,
    Elephant: 0,
    Soldier: 0,
  };
  for (const row of state.board) {
    for (const cell of row) {
      if (cell && cell.owner === side) counts[cell.kind] += 1;
    }
  }
  return counts;
}

/** `legal` から from が一致する全 to 候補を返す (UI for from-click → to-targets)。 */
export function janggiLegalTargetsFor(
  legal: JanggiMove[],
  from: [number, number],
): { Move: { from: [number, number]; to: [number, number] } }[] {
  return legal.filter((m): m is { Move: { from: [number, number]; to: [number, number] } } => {
    if (!isJanggiMove(m)) return false;
    return m.Move.from[0] === from[0] && m.Move.from[1] === from[1];
  });
}
